import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmailStructure } from 'src/app/core/interfaces/emailStructure';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Patterns } from 'src/app/core/resources/patterns';
import { Permission } from 'src/app/core/resources/permission';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CompaniesService } from '../list-companies.service';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { CatalogService } from 'src/app/core/services/catalog.service';
import { Catalog } from 'src/app/core/interfaces/catalog';
import { CatalogItem } from 'src/app/core/interfaces/catalogItem';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthService } from 'src/app/core/services/authentication.service';
import { EmailType } from 'src/app/core/enums/email.enum';
import { Companies } from 'src/app/core/resources/companies';

@Component({
  selector: 'app-email-list',
  templateUrl: './email-list.component.html',
  styleUrls: ['./email-list.component.scss']
})
export class EmailListComponent implements OnInit {

  permission = Permission;
  form: FormGroup;
  types: CatalogItem[] = [];

  @Input() nitCompany: string;
  @Input() isPrincipalCompany: boolean;
  @Output() public refreshList: EventEmitter<any> = new EventEmitter();
  localEmails: EmailStructure[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private patterns: Patterns,
    public utils: Utils,
    private snackBarService: SnackBarService,
    public spinner: NgxSpinnerService,
    public listCompaniesService: CompaniesService,
    private permissionRole: PermissionRole,
    private catalogService: CatalogService,
    public dialog: MatDialog,
    private authService: AuthService

  ) {
  }

  async ngOnInit() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.pattern(this.patterns.EMAIL.source), Validators.maxLength(100)]],
      type: ['', Validators.required]
    });
    await this.setTypes();
    await this.loadEmails();
  }

  private async loadEmails(): Promise<void> {
    if (!this.nitCompany) {
      this.localEmails = [];
      return;
    }

    try {
      const emails = await this.listCompaniesService.getEmailsFromCompany(this.nitCompany).toPromise();
      this.localEmails = emails || [];
    } catch (error) {
      this.localEmails = [];
    }
  }

  async setTypes(): Promise<void> {
    try {
      const types = await this.catalogService.getCatalog('EmailType').toPromise();
      let allTypes = types && types.catalog && types.catalog.length > 0 ? types.catalog : [];
      this.types = this.filterTypesBySaasCompany(allTypes);
    } catch (error) {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, "Aceptar", "error");
      this.types = [];
    }
  }

  public get isTeclogiUser(): boolean {
    return this.authService && this.authService.getCompany() &&
      Companies.companiesNIT.TECLOGI === this.authService.getCompany().companyId;
  }

  private filterTypesBySaasCompany(allTypes: CatalogItem[]): CatalogItem[] {
    if (!this.isTeclogiUser) {
      allTypes = allTypes.filter(type => type.name !== EmailType.REPORT_BATCH_FAST_PAYMENT);
    }
    if (this.isPrincipalCompany) {
      return allTypes;
    }
    const clientEmailsTypes = [
      EmailType.TRACKING,
      EmailType.SEND_NOTIFICATION_PUSH_FROM_TRACKING,
      EmailType.DELETE_CARGO_NOTIFICATION,
      EmailType.CONFIRMED_LICENSE_PLATE,

    ]
    return allTypes.filter(type => clientEmailsTypes.includes(type.name as EmailType));
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      if (this.utils.errorMessagesCustomized(this.form.get('name'), 'correo electrónico', null, 100)) return;
      if (this.utils.errorMessagesCustomized(this.form.get('type'), 'tipo')) return;
      this.snackBarService.openSnackBar(FormMessages.GENERAL_ERROR_DEFAULT, undefined, 'alert');
      return;
    }
    const name = this.form.get('name').value;
    const type = this.form.get('type').value;
    if (this.isDuplicateEmail(name, type)) return;
    const body: EmailStructure = this.form.value;
    this.addEmailToCompany(body);
  }

  private addEmailToCompany(body: EmailStructure) {
    this.spinner.show();
    const $sub = this.listCompaniesService.addEmailToCompany(body, this.nitCompany).subscribe(
      {
        next: () => {
          this.snackBarService.openSnackBar("Correo agregado exitosamente");
          this.loadEmails();
          this.spinner.hide();
        },
        error: () => {
          this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
          this.spinner.hide();
        },
        complete: () => {
          $sub.unsubscribe();
        }
      }
    )
  }

  getActualEmails(type): Array<EmailStructure> {
    return this.localEmails.filter(email => email.type === type);
  }

  get hasPermissionToAdd(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.administration.module,
      this.permission.administration.createEmailCompany
    );
  }

  onDeleteEmail(email: EmailStructure): void {
    const dialogConfig = new MatDialogConfig();
    let title: string = '';
    title = `¿Estas seguro que deseas eliminar ${email.name ? `el correo ${email.name}` : 'el correo'}?`;
    dialogConfig.data = {
      title: title,
    };
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    const dialogRef = this.dialog.open(DialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.state) {
        this.deleteEmailFromCompany(email);
      }
    });
  }

  private deleteEmailFromCompany(email: EmailStructure): void {
    this.spinner.show();
    const emailId = email.id;
    const $sub = this.listCompaniesService.deleteEmailFromCompany(this.nitCompany, emailId).subscribe({
      next: () => {
        this.snackBarService.openSnackBar("Correo eliminado exitosamente");
        this.loadEmails();
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error al eliminar correo:', error);
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
        this.spinner.hide();
      },
      complete: () => {
        $sub.unsubscribe();
      }
    });
  }

  isDuplicateEmail(name: string, type: string): boolean {
    const normalizedName = name.toLowerCase().trim();
    const isDuplicate = this.localEmails.some(email => email.name.toLowerCase() === normalizedName && email.type === type);
    if (isDuplicate) {
      this.snackBarService.openSnackBar("El correo electrónico ya existe", undefined, 'error');

    }
    return isDuplicate;
  }

  get hasPermissionToDelete(): boolean {
    const hasPermission = this.permissionRole.hasPermission(
      this.permission.administration.module,
      this.permission.administration.deleteEmailCompany
    );
    return hasPermission;
  }

}
